import { Component, effect, inject, OnInit } from '@angular/core';
import { FileUploadEvent, FileUploadModule } from 'primeng/fileupload';
import { CommuneService } from '../../../services/commune/commune.service';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { NgIf } from '@angular/common';
import { GlobalsStore } from '../../../stores/globals.state';
import { MenuItem, MessageService } from 'primeng/api';
import { AtlasConfiguration, Commune } from '../../../models/commune';
import { CheckboxModule } from 'primeng/checkbox';
import { PaginatorModule } from 'primeng/paginator';
import { DividerModule } from 'primeng/divider';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { ColorsetDefaultsService } from '../../../services/colorset/colorset-defaults.service';
import { LogoComponent } from '../../atoms/logo/logo.component';
import { ColorsetDropdownComponent } from '../../atoms/colorset-dropdown/colorset-dropdown.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { RadioButtonModule } from 'primeng/radiobutton';

@Component({
  selector: 'app-configuration-atlas',
  standalone: true,
  imports: [
    FileUploadModule,
    FormFieldWrapperComponent,
    FormsModule,
    InputTextModule,
    NgIf,
    ReactiveFormsModule,
    CheckboxModule,
    PaginatorModule,
    DividerModule,
    LogoComponent,
    ColorsetDropdownComponent,
    HeaderComponent,
    RadioButtonModule,
  ],
  templateUrl: './configuration-atlas.component.html',
  styleUrl: './configuration-atlas.component.scss',
})
export class ConfigurationAtlasComponent implements OnInit {
  readonly communeStore = inject(CommuneStore);
  readonly globalStore = inject(GlobalsStore);
  readonly defaultsService = inject(ColorsetDefaultsService);
  form: FormGroup = new FormGroup({});
  initialized = false;
  items: MenuItem[] = [];
  activeCommune: Commune | null = null;
  atlasConfiguration?: AtlasConfiguration;

  constructor(
    public communeService: CommuneService,
    private fb: FormBuilder,
    private messageService: MessageService
  ) {
    effect(() => {
      if (!this.activeCommune) {
        this.activeCommune = this.globalStore.activeCommune();
        this.atlasConfiguration = this.activeCommune?.atlasConfig;
        this.initForm();
      }
    });
  }

  ngOnInit() {
    this.initForm();
  }

  /**
   * Initializes the form with default or pre-configured values from the atlasConfiguration object.
   * The form contains fields for headline, colorset, startTeaser, absoluteDecimals, and relativeDecimals.
   *
   * @return {void} Executes the initialization of the form and sets the form property.
   */
  initForm(): void {
    this.form = this.fb.group({
      colorset: [this.atlasConfiguration?.colorset || ''],
      absoluteDecimals: [this.atlasConfiguration?.absoluteDecimals || 0],
      relativeDecimals: [this.atlasConfiguration?.relativeDecimals || 1],
      allowWegweiserData: [
        this.atlasConfiguration?.allowWegweiserData || 'all',
      ],
    });

    this.form.valueChanges.subscribe(() => {
      this.submit();
    });
  }

  /**
   * Handles the upload event and updates the active commune's logo configuration.
   * Displays success or error messages based on the upload operation's status.
   *
   * @param {FileUploadEvent | any} $event - The upload event containing data about the uploaded file and its status.
   * @return {void} No return value.
   */
  onUpload($event: FileUploadEvent | any): void {
    if (!this.activeCommune) return;
    if ($event.originalEvent.status !== 200) {
      this.messageService.add({
        severity: 'danger',
        summary: 'Upload fehlgeschlagen',
        detail: $event.originalEvent.body.message,
      });
    } else {
      console.log($event);
      this.activeCommune.atlasConfig.logo =
        $event.originalEvent.body.filePath || '';
      this.communeStore.update(this.activeCommune);

      this.messageService.add({
        severity: 'info',
        summary: 'Upload erfolgreich!',
      });
    }
  }

  /**
   * Submits the current form data and updates the 'activeCommune' object with the new values.
   * If 'activeCommune' is not defined, no action will be taken.
   *
   * @return {void} This method does not return a value.
   */
  submit(): void {
    if (!this.activeCommune) return;

    this.activeCommune.atlasConfig = {
      ...this.activeCommune.atlasConfig,
      ...this.form.value,
    };
    this.communeStore.update(this.activeCommune);
  }
}
